import {
  Box,
  DropdownButton,
  FlexBox,
  Timestamp,
} from '@dixa/lib-shared-habla';
import cx from 'classnames';
import ArticleMenu from 'collection/pages/collectionEdit/components/articleMenu/articleMenu';
import { RestrictionsBadge } from 'collection/pages/collectionEdit/components/restrictionsBadge';
import { Article } from 'lib-knowledge-management-common/models/types';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { useTypedParams } from 'lib-knowledge-utils/hooks/useTypedParams';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { RefObject } from 'react';
import { Link } from 'react-router-dom';

import { NodeLockIconWithTooltip } from 'components/icons/nodeLockIcon/nodeLockIcon';
import { NonDefaultLanguageBadge } from 'components/nonDefaultLanguageBadge';
import useCollectionTreeFetch from 'hooks/useCollectionTreeFetch';

import { DragHandle } from '../dragHandle';

import MetaText from './metaText';
import styles from './styles.module.scss';

const TimeAgo = ({ node }: { node: Article }) => (
  <>
    Edited{' '}
    <Timestamp
      time={node.updatedAt / 1000}
      refreshRate={60000}
      relative={true}
    />
    {node.indexedAt && (
      <>
        {' '}
        • Published{' '}
        <Timestamp
          time={node.indexedAt / 1000}
          refreshRate={60000}
          relative={true}
        />
      </>
    )}
  </>
);

type Props = {
  node: Article;
  handleRef?: RefObject<HTMLSpanElement>;
  isDragging?: boolean;
  depth: number;
};

const ArticleNode = ({
  node,
  isDragging,
  handleRef,
  depth,
}: Props): JSX.Element => {
  const articleStyles = cx(styles.articleNode, {
    [styles.nodeIsDragging]: isDragging,
    [styles.isSyncedWithElevio]: node.isSyncedWithElevio,
  });
  const dropdownStyles = cx(isDragging && styles.isHidden);
  const { collectionId } = useTypedParams(['collectionId']);
  const { data } = useCollectionTreeFetch({ collectionId });

  if (!node.parent) throw new Error('No category found for ArticleNode');

  const nodeTitle = extractTitleFromTranslations(node.titles);

  return (
    <Box className={articleStyles} style={{ marginLeft: `${depth * 32}px` }}>
      <FlexBox>
        {!data || data.collection?.isSyncedWithElevio ? null : (
          <DragHandle ref={handleRef} />
        )}
        <Box className={styles.contentWrapper}>
          <Link
            to={routeGenerator.articleEdit({
              articleId: node.id,
              collectionId,
            })}
          >
            <span className={styles.title} data-testid="article-node">
              {nodeTitle}
              <NonDefaultLanguageBadge titles={node.titles} />
            </span>
          </Link>
          <MetaText>
            <TimeAgo node={node} />
          </MetaText>
        </Box>
      </FlexBox>
      <FlexBox justifyContent="flex-end" alignItems="center" gap="200">
        <RestrictionsBadge restrictions={node.restrictions} />
        {data?.collection?.isSyncedWithElevio && node.isSyncedWithElevio && (
          <NodeLockIconWithTooltip label="Synced from Elevio" />
        )}
        <DropdownButton
          className={dropdownStyles}
          icon="dots-h"
          buttonProps={{ variant: 'tertiary', disabled: !data }}
          dropdown={
            <ArticleMenu
              articleId={node.id}
              isSyncedWithElevio={data?.collection?.isSyncedWithElevio || false}
            />
          }
        />
      </FlexBox>
    </Box>
  );
};

export default ArticleNode;
